import React, { useCallback } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import toast from 'react-hot-toast';

import appleBlackLogoSrc from '@assets/images/apple_black_logo.svg';

import { useAppContext } from '@contexts/AppContext';
import useOauthCreateUser from '@hooks/useOauthCreateUser';
import useOauthLogin from '@hooks/useOauthLogin';
import useOauthUnlockContent from '@hooks/useOauthUnlockContent';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { APPLE_PROVIDER, AppleEventName } from '@utils/backend-api/oauth';
import { LeadType } from '@utils/types';

const APPLE_EVENT_CLOSE_POPUP = 'popup_closed_by_user';

interface AppleSignInProps {
  authorization: {
    id_token: string;
  };
}

interface MarketingLeadCaptureWithAppleProps {
  articleId?: number;
  briefingGuid?: string;
  eventName: AppleEventName;
  formLocation: string;
  isGdprOptIn: boolean;
  leadType: LeadType;
  newsletterIds?: number[];
  redirectToOnSuccess?: string;
  shareGuid?: string;
}

export default function MarketingLeadCaptureWithApple({
  articleId,
  briefingGuid,
  eventName,
  formLocation,
  isGdprOptIn,
  leadType,
  newsletterIds,
  redirectToOnSuccess,
  shareGuid,
}: MarketingLeadCaptureWithAppleProps) {
  const hydrated = useServerSideRendering();
  const { login } = useOauthLogin();
  const { createUser } = useOauthCreateUser();
  const { unlockContentViaOauth } = useOauthUnlockContent();
  const { isGdprRequired } = useAppContext();

  const handleErrorResponse = (event) => {
    if (event.error !== APPLE_EVENT_CLOSE_POPUP) {
      toast.error('No response from Apple. Please try again.');
    }
  };

  const handleAppleSignIn = useCallback(
    async (data: AppleSignInProps) => {
      const idToken = data.authorization.id_token;
      const commonPayload = {
        id_token: idToken,
        form_location: formLocation,
        page: window.location.pathname,
        provider: APPLE_PROVIDER,
      };

      if (eventName === AppleEventName.LogIn) {
        login({
          ...commonPayload,
          returning_url: redirectToOnSuccess,
        });
      } else if (eventName === AppleEventName.CreateLead) {
        createUser({
          ...commonPayload,
          lead_type: leadType,
          declined_opt_in: isGdprRequired ? !isGdprOptIn : false,
          redirect_to_on_success: redirectToOnSuccess,
          opt_in_required: isGdprRequired,
          newsletter_ids: newsletterIds,
        });
      } else if (eventName === AppleEventName.UnlockContent) {
        unlockContentViaOauth({
          ...commonPayload,
          article_id: articleId,
          lead_type: leadType,
          briefing_guid: briefingGuid,
          share_guid: shareGuid,
          newsletter_ids: newsletterIds,
          user: {
            declined_opt_in: isGdprRequired ? !isGdprOptIn : false,
          },
          opt_in_required: isGdprRequired,
        });
      }
    },
    [
      login,
      createUser,
      unlockContentViaOauth,
      eventName,
      leadType,
      formLocation,
      redirectToOnSuccess,
      isGdprRequired,
      isGdprOptIn,
      articleId,
      briefingGuid,
      shareGuid,
      newsletterIds,
    ]
  );

  return (
    hydrated && (
      <AppleSignin
        authOptions={{
          clientId: document
            .querySelector("meta[name='appleid-signin-client-id']")
            .getAttribute('content'),
          scope: 'name email',
          redirectURI: document
            .querySelector("meta[name='appleid-signin-redirect-uri']")
            .getAttribute('content'),
          usePopup: true,
        }}
        uiType="light"
        onSuccess={handleAppleSignIn}
        onError={handleErrorResponse}
        noDefaultStyle
        render={(renderProps) => (
          <button
            {...renderProps}
            className="btn-google-signin font-google-signin relative flex h-40 w-[250px] items-center overflow-hidden rounded-4 border border-solid px-12 text-button-sm"
          >
            <div className="absolute inset-0"></div>
            <img src={appleBlackLogoSrc} className="relative w-[18px]" />
            <div className="font-google-signin relative grow text-center">Continue with Apple</div>
          </button>
        )}
      />
    )
  );
}
